import React, { useMemo } from 'react';
import { themes } from '../config/themes';
import { getEventGridDayKeys, getLocalDateKey } from '../utils/eventUtils';
import SkylineWatermark from './calendar/SkylineWatermark';
import CalendarLegend from './calendar/CalendarLegend';
import EventChip from './calendar/EventChip';
import { fs, sz } from '../utils/typography';

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MAX_CHIPS = 3;
// Landscape phones (`compact`): ~40px of chip space per cell in a 5-week
// month, ~30px in a 6-week one.
const COMPACT_MAX_CHIPS = { 5: 2, 6: 1 };

// Month view — "Atlanta Editorial" design refresh: charcoal weekday band,
// hairline grid of translucent day cells over the bottom-anchored skyline
// watermark, bar-treatment event chips, accent today pill, legend footer.
//
// `compact` is a phone held sideways (see useCompactLandscape): cells show
// one or two title-only chips with a "+N" beside the day number, instead of
// the count badge portrait phones get or the 3-chip desktop cell. The chips
// are display-only there: tapping anywhere in the cell opens that day's list.
const MonthView = ({ compact = false, curMonth, curYear, events, theme, onDaySelect, onEventSelect, selectedDay, legendEvents, selectedCategory, selectedTags, onCategoryClick, onTagClick }) => {
    const tokens = theme?.tokens || themes.light.tokens;
    const c = tokens.colors;

    const firstDay = new Date(curYear, curMonth - 1, 1).getDay();
    const lastDay = new Date(curYear, curMonth, 0).getDate();
    const curMonthName = new Date(curYear, curMonth - 1, 1).toLocaleString('default', { month: 'long' });
    const totalCells = lastDay + firstDay > 35 ? 42 : 35;
    const rows = totalCells / 7;

    const eventsByDay = useMemo(() => {
        const map = {};
        // Short multi-day events repeat on each day they cover.
        (events || []).forEach((event) => {
            getEventGridDayKeys(event).forEach((key) => {
                (map[key] = map[key] || []).push(event);
            });
        });
        return map;
    }, [events]);

    const todayKey = getLocalDateKey(new Date());
    const selectedKey = selectedDay ? getLocalDateKey(selectedDay) : null;

    return (
        <div
            className="w-full h-full flex flex-col"
            style={{ background: c.surface, color: c.ink, fontFamily: tokens.fonts.ui, textAlign: 'left' }}
            role="region"
            aria-label={`Calendar for ${curMonthName} ${curYear}`}
        >
            {/* charcoal weekday header band */}
            <div
                className="grid grid-cols-7 flex-none"
                style={{ background: c.headBg, borderBottom: `1px solid ${c.gridLine}`, position: 'relative', zIndex: 1 }}
                role="row"
            >
                {DOW.map((day) => (
                    <div
                        key={day}
                        className="text-center py-2 px-1"
                        style={{
                            fontFamily: tokens.fonts.ui, fontSize: fs(11), fontWeight: 600,
                            letterSpacing: 0.8, textTransform: 'uppercase', color: c.headInk,
                        }}
                        role="columnheader"
                    >
                        <span className="hidden lg:inline">{day}</span>
                        <span className="inline lg:hidden">{day[0]}</span>
                    </div>
                ))}
            </div>

            {/* day grid over the skyline watermark */}
            <div
                className="flex-1 min-h-0 grid grid-cols-7"
                style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, position: 'relative' }}
                role="grid"
                aria-label={`Days in ${curMonthName} ${curYear}`}
            >
                <SkylineWatermark tokens={tokens} />
                {Array.from({ length: totalCells }, (_, i) => {
                    const dayNum = i - firstDay + 1;
                    const inMonth = dayNum >= 1 && dayNum <= lastDay;
                    const col = i % 7;
                    const row = Math.floor(i / 7);

                    if (!inMonth) {
                        return <div key={`blank-${i}`} role="gridcell" aria-hidden="true" style={{ background: 'transparent' }} />;
                    }

                    const thisDate = new Date(curYear, curMonth - 1, dayNum);
                    const dateKey = getLocalDateKey(thisDate);
                    const dayEvents = eventsByDay[dateKey] || [];
                    const isToday = dateKey === todayKey;
                    const isSelected = dateKey === selectedKey;
                    const shown = dayEvents.slice(0, compact ? (COMPACT_MAX_CHIPS[rows] || 1) : MAX_CHIPS);
                    const extra = dayEvents.length - shown.length;

                    return (
                        <div
                            key={dateKey}
                            className="flex flex-col min-w-0 overflow-hidden cursor-pointer"
                            style={{
                                borderLeft: col === 0 ? 'none' : `1px solid ${c.gridLine}`,
                                borderTop: row === 0 ? 'none' : `1px solid ${c.gridLine}`,
                                background: isSelected
                                    ? `color-mix(in srgb, ${c.selBg} 84%, transparent)`
                                    : `color-mix(in srgb, ${c.surface} 60%, transparent)`,
                                // The ring is the selected-day indicator (≥3:1 against the
                                // cell in both themes, WCAG 1.4.11); the tint is decorative.
                                boxShadow: isSelected ? `inset 0 0 0 2px ${c.selRing}` : 'none',
                                padding: compact ? '3px 5px 2px' : '6px 7px 5px', position: 'relative', zIndex: 1,
                            }}
                            role="gridcell"
                            aria-selected={isSelected}
                            aria-current={isToday ? 'date' : undefined}
                            tabIndex={0}
                            onClick={() => onDaySelect(thisDate)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onDaySelect(thisDate);
                                }
                            }}
                            aria-label={`${thisDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${dayEvents.length > 0 ? `, ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}` : ''}`}
                        >
                            <div className="flex items-center justify-between flex-none" style={{ marginBottom: compact ? 1 : 3 }}>
                                <span style={{
                                    fontFamily: tokens.fonts.ui, fontSize: fs(12.5), fontWeight: 600, lineHeight: 1,
                                    color: isToday ? c.todayInk : c.ink,
                                    ...(isToday ? {
                                        background: c.today, borderRadius: sz(13), minWidth: sz(21), height: sz(21),
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                                    } : { padding: compact ? '2px 2px' : '4px 2px' }),
                                }}>{dayNum}</span>
                                {compact && extra > 0 && (
                                    <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(10), fontWeight: 700, color: c.accent }} aria-hidden="true">
                                        +{extra}
                                    </span>
                                )}
                            </div>

                            {/* event chips — desktop */}
                            <div
                                className={compact ? 'flex flex-col flex-1 min-h-0' : 'hidden lg:flex flex-col flex-1 min-h-0'}
                                style={{ gap: compact ? 1 : 2, paddingBottom: extra > 0 && !compact ? sz(22) : 0 }}
                            >
                                {shown.map((event) => (
                                    <EventChip
                                        key={event.id}
                                        event={event}
                                        tokens={tokens}
                                        compact={compact}
                                        interactive={!compact}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDaySelect(thisDate);
                                            onEventSelect(event, e, thisDate);
                                        }}
                                    />
                                ))}
                            </div>

                            {extra > 0 && !compact && (
                                <button
                                    type="button"
                                    className="hidden lg:flex items-center justify-center"
                                    style={{
                                        position: 'absolute', left: 0, right: 0, bottom: 0, height: sz(22),
                                        border: 'none', borderTop: `1px solid ${c.gridLine}`,
                                        background: `color-mix(in srgb, ${c.accent} ${tokens.mode === 'dark' ? '22%' : '12%'}, ${c.surfaceAlt})`,
                                        color: c.accent, cursor: 'pointer',
                                        fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 700,
                                        letterSpacing: 0.4, textTransform: 'uppercase',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDaySelect(thisDate);
                                    }}
                                    aria-label={`Show ${extra} more event${extra > 1 ? 's' : ''} for ${thisDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
                                >
                                    +{extra} more
                                </button>
                            )}

                            {/* event count pill — small screens */}
                            {dayEvents.length > 0 && !compact && (
                                <div className="flex lg:hidden items-center justify-center flex-1">
                                    <span style={{
                                        fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 600,
                                        color: c.accent, border: `1px solid ${c.accent}`, borderRadius: 11,
                                        minWidth: 22, padding: '2px 6px', textAlign: 'center',
                                    }}>{dayEvents.length}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <CalendarLegend
                tokens={tokens}
                events={legendEvents}
                selectedCategory={selectedCategory}
                selectedTags={selectedTags}
                onCategoryClick={onCategoryClick}
                onTagClick={onTagClick}
            />
        </div>
    );
};

export default MonthView;
